'use strict';

import { queryAll, hasClass, matchesBreakpoint } from '../domUtil';
import { loadLazyVideo } from '../imageLazyLoad';
import video from './video';

const { getActiveVideoEl, updateVideoControlState } = video;

const USER_PAUSED_ATTR = 'data-user-paused';
const HIDDEN_SLIDE_INDEX = 4; // slides 5+ are hidden until gallery expand (nth-of-type(n+5))
const PDP_VIDEO_SELECTOR = '.video-component.video-PDP, .video-carousel-item .video-component';

/**
 * @param {Element} videoComponent
 * @returns {boolean}
 */
export function isPdpGalleryVideoComponent(videoComponent) {
    if (!videoComponent) return false;
    return hasClass(videoComponent, 'video-PDP') || Boolean(videoComponent.closest('.video-carousel-item'));
}

/**
 * @param {Element} videoComponent
 */
export function setUserPaused(videoComponent) {
    if (isPdpGalleryVideoComponent(videoComponent)) {
        videoComponent.setAttribute(USER_PAUSED_ATTR, 'true');
    }
}

/**
 * @param {Element} videoComponent
 */
export function clearUserPaused(videoComponent) {
    videoComponent?.removeAttribute(USER_PAUSED_ATTR);
}

/**
 * @param {Element} gallery
 * @returns {boolean}
 */
function isSetSwiperGallery(gallery) {
    return Boolean(
        gallery &&
        (gallery.closest('.product-set-detail') || gallery.closest('.product-set-item-image-carousel'))
    );
}

/**
 * @param {Element} videoComponent
 * @returns {boolean}
 */
function isInPdpGallery(videoComponent) {
    return Boolean(
        videoComponent.closest('.js-swiper-main') ||
        videoComponent.closest('.pdp-gallery') ||
        videoComponent.closest('.product-set-item-image-carousel')
    );
}

/**
 * @param {Element} videoComponent
 * @returns {boolean}
 */
function isSlideVisibleForAutoplay(videoComponent) {
    const slide = videoComponent.closest('.swiper-slide');
    if (!slide) return true;

    const style = getComputedStyle(slide);
    if (style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
    }

    const gallery = videoComponent.closest('.js-swiper-main');
    if (gallery && isSetSwiperGallery(gallery)) {
        return hasClass(slide, 'swiper-slide-visible');
    }

    if (!matchesBreakpoint('md')) {
        return true;
    }

    const primaryImages = slide.closest('.primary-images');
    if (!primaryImages || hasClass(primaryImages, 'm-expanded')) {
        return true;
    }

    if (slide.closest('.product-set-detail') || slide.closest('.product-set-item-image-carousel')) {
        return true;
    }

    const wrapper = slide.parentElement;
    if (!wrapper) return true;

    const slideIndex = Array.prototype.indexOf.call(wrapper.children, slide);
    return slideIndex < HIDDEN_SLIDE_INDEX;
}

/**
 * @param {HTMLVideoElement} videoEl
 */
function ensurePdpVideoAttributes(videoEl) {
    if (!videoEl.hasAttribute('autoplay')) {
        videoEl.setAttribute('autoplay', '');
    }
    videoEl.muted = true;
}

/**
 * @param {Element} videoComponent
 * @returns {boolean}
 */
function shouldSyncPlay(videoComponent) {
    if (!isPdpGalleryVideoComponent(videoComponent)) return false;
    if (videoComponent.hasAttribute(USER_PAUSED_ATTR)) return false;
    return isSlideVisibleForAutoplay(videoComponent);
}

/**
 * @param {Element} videoComponent
 */
async function syncOnePdpGalleryVideo(videoComponent) {
    const videoEl = getActiveVideoEl(videoComponent);
    if (!videoEl) return;

    if (shouldSyncPlay(videoComponent)) {
        ensurePdpVideoAttributes(videoEl);
        await loadLazyVideo(videoEl);

        try {
            await videoEl.play();
            updateVideoControlState(videoComponent, true);
        } catch {
            updateVideoControlState(videoComponent, false);
        }
        return;
    }

    if (!videoEl.paused) {
        videoEl.pause();
    }
    updateVideoControlState(videoComponent, false);
}

/**
 * Sync PDP gallery videos after slide change, color change, or gallery expand.
 * Relies on autoplay/muted on the video tag for initial visible playback on page load.
 *
 * @param {Document|Element} root
 */
export function syncPdpGalleryVideos(root = document) {
    queryAll(PDP_VIDEO_SELECTOR, root).forEach(component => {
        if (!isInPdpGallery(component)) return;
        syncOnePdpGalleryVideo(component);
    });
}
